// external
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

// interstate components
import type { TabProps } from '@interstate/components/Tabs';
import { Tabs } from '@interstate/components/Tabs';
import { Box } from '@interstate/components/Box';

// internal components
import { MileageCostOptionsHeader } from './MileageCostOptionsHeader';
import { MileageCostOptionsTabContent } from './MileageCostOptionsTabContent';
import { ResidualBreakdownTabContent } from './ResidualBreakdownTabContent';

// types
import type { MileageCostOptionsProps, MileageCostOptionsTabContentRef } from './mileageCostOptionsTypes';

// constants
import { TAB_IDS, TAB_LABELS } from './constants';

import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { SlideOutWithFixedFooter } from '../utils/SlideOutWithFixedFooter';

const MileageCostOptionsSlideOut: FC<MileageCostOptionsProps> = ({ show, onHide }) => {
    const isSmallScreen = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` });

    const panelWidth = isSmallScreen ? '100%' : '717px';

    // Ref to mileage tab for apply logic
    const mileageTabRef = useRef<MileageCostOptionsTabContentRef>(null);

    // Feature toggle for residual breakdown
    const isResidualBreakdownEnabled = useSelector(featureToggleSelectors.isResidualBreakdownEnabled);

    // Tab state
    const [activeTab, setActiveTab] = useState<string>(TAB_IDS.MILEAGE_COST_OPTIONS);

    // Track canApply state from mileage tab
    const [canApply, setCanApply] = useState(false);

    // Callback from mileage tab to update canApply state
    const handleCanApplyChange = (value: boolean) => {
        setCanApply(value);
    };

    const handleChangeActiveTab = (tabIndex: number) => {
        const tabId = tabIndex === 0 ? TAB_IDS.MILEAGE_COST_OPTIONS : TAB_IDS.RESIDUAL_BREAKDOWN;
        setActiveTab(tabId);
    };

    const handleApplyChanges = () => {
        /* istanbul ignore next -- defensive ref check */
        mileageTabRef.current?.apply();
        onHide();
    };

    const handleCancel = () => {
        onHide();
    };

    // Tabs configuration - conditionally include residual breakdown tab based on feature toggle
    const tabs: TabProps[] = [
        {
            label: TAB_LABELS.MILEAGE_COST_OPTIONS,
            active: activeTab === TAB_IDS.MILEAGE_COST_OPTIONS,
            id: TAB_IDS.MILEAGE_COST_OPTIONS,
            component: <MileageCostOptionsTabContent ref={mileageTabRef} onCanApplyChange={handleCanApplyChange} />
        },
        ...(isResidualBreakdownEnabled
            ? [
                  {
                      label: TAB_LABELS.RESIDUAL_BREAKDOWN,
                      active: activeTab === TAB_IDS.RESIDUAL_BREAKDOWN,
                      id: TAB_IDS.RESIDUAL_BREAKDOWN,
                      component: <ResidualBreakdownTabContent />
                  }
              ]
            : [])
    ];

    return (
        <SlideOutWithFixedFooter
            show={show}
            onHide={onHide}
            header={isResidualBreakdownEnabled ? <MileageCostOptionsHeader /> : 'Mileage Cost Options'}
            position="right"
            data-testid="mileage-cost-options-slideout"
            panelWidth={panelWidth}
            footer={{
                layout: 'rightAligned',
                options: [{ action: handleCancel, label: 'Cancel' }],
                primary: {
                    action: handleApplyChanges,
                    label: 'Apply',
                    disabled: !canApply
                }
            }}
        >
            {isResidualBreakdownEnabled ? (
                <Box>
                    <Tabs
                        id="mileage-residual-tabs"
                        sx={{
                            position: 'sticky !important',
                            top: '0',
                            zIndex: '10',
                            backgroundColor: '#FFF'
                        }}
                        tabs={tabs}
                        onActivate={handleChangeActiveTab}
                        data-testid="mileage-residual-tabs"
                    />
                </Box>
            ) : (
                <MileageCostOptionsTabContent ref={mileageTabRef} onCanApplyChange={handleCanApplyChange} />
            )}
        </SlideOutWithFixedFooter>
    );
};

export default MileageCostOptionsSlideOut;
